import { supabase } from "@/integrations/supabase/client";
import { BoardCategory, Post } from "@/types/board";

export const getPosts = async (category?: BoardCategory) => {
  let query = supabase
    .from('board_posts')
    .select('*');
    
  if (category) {
    query = query.eq('category', category);
  }
  
  const { data, error } = await query.order('created_at', { ascending: false });
  
  if (error) {
    console.error('Error fetching posts:', error);
    throw error;
  }
  
  return data as Post[];
};

export const getPostById = async (id: string) => {
  const { data, error } = await supabase
    .from('board_posts')
    .select('*')
    .eq('id', id)
    .single();
    
  if (error) {
    console.error('Error fetching post:', error);
    throw error;
  }
  
  await incrementViewCount(id, data.view_count);
  
  return data as Post;
};

export const getCommentsByPostId = async (postId: string) => {
  const { data, error } = await supabase
    .from('post_comments')
    .select('*')
    .eq('post_id', postId)
    .order('created_at', { ascending: true });
    
  if (error) {
    console.error('Error fetching comments:', error);
    throw error;
  }
  
  return data;
};

export const addComment = async (postId: string, content: string, user: any) => {
  const { data: profileData, error: profileError } = await supabase
    .from('profiles')
    .select('username')
    .eq('id', user.id)
    .single();
  
  let username;
  if (profileError) {
    console.error('Error fetching profile:', profileError);
    username = user.email?.split('@')[0] || 'Anonymous';
  } else {
    username = profileData.username || user.email?.split('@')[0] || 'Anonymous';
  }
  
  const commentData = {
    post_id: postId,
    content,
    author_id: user.id,
    author_name: username,
    author_avatar: user.user_metadata?.avatar_url || '/placeholder.svg'
  };
  
  const { data: comment, error: commentError } = await supabase
    .from('post_comments')
    .insert(commentData)
    .select()
    .single();
    
  if (commentError) {
    console.error('Error adding comment:', commentError);
    throw commentError;
  }
  
  const { data: post } = await supabase
    .from('board_posts')
    .select('comment_count')
    .eq('id', postId)
    .single();
    
  const { error: updateError } = await supabase
    .from('board_posts')
    .update({ comment_count: (post.comment_count || 0) + 1 })
    .eq('id', postId);
    
  if (updateError) {
    console.error('Error updating comment count:', updateError);
  }
  
  return comment;
};

export const createPost = async (
  title: string, 
  content: string, 
  category: BoardCategory, 
  user: any | null
) => {
  const postData: any = {
    title,
    content,
    category,
    author_name: 'Anonymous'
  };
  
  if (user) {
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', user.id)
      .single();
      
    if (profileError) {
      console.error('Error fetching user profile:', profileError);
    }
    
    const username = profile?.username || user.email?.split('@')[0] || 'Anonymous';
    
    postData.author_id = user.id;
    postData.author_name = username;
    postData.author_avatar = user.user_metadata?.avatar_url || '/placeholder.svg';
  }
  
  const { data, error } = await supabase
    .from('board_posts')
    .insert(postData)
    .select()
    .single();
    
  if (error) {
    console.error('Error creating post:', error);
    throw error;
  }
  
  return data;
};

export const toggleLike = async (postId: string, currentLikes: number) => {
  const { error } = await supabase
    .from('board_posts')
    .update({ like_count: currentLikes })
    .eq('id', postId);
    
  if (error) {
    console.error('Error updating like count:', error);
    throw error;
  }
  
  return true;
};

export const toggleCommentLike = async (commentId: string, currentLikes: number) => {
  const { error } = await supabase
    .from('post_comments')
    .update({ like_count: currentLikes })
    .eq('id', commentId);
    
  if (error) {
    console.error('Error updating comment like count:', error);
    throw error;
  }
  
  return true;
};

const incrementViewCount = async (postId: string, currentViews: number) => {
  const randomIncrement = Math.floor(Math.random() * 12) + 4; // Random number between 4 and 15
  
  try {
    const { error } = await supabase
      .from('board_posts')
      .update({ view_count: currentViews + randomIncrement })
      .eq('id', postId);
      
    if (error) {
      console.error('Error updating view count:', error);
    }
  } catch (error) {
    console.error('Failed to increment view count:', error);
  }
};

export const getUserPosts = async (userId: string, page = 1, limit = 10) => {
  const start = (page - 1) * limit;
  const end = start + limit - 1;
  
  const { data, error, count } = await supabase
    .from('board_posts')
    .select('*', { count: 'exact' })
    .eq('author_id', userId)
    .order('created_at', { ascending: false })
    .range(start, end);
    
  if (error) {
    console.error('Error fetching user posts:', error);
    throw error;
  }
  
  return { 
    posts: data as Post[], 
    totalCount: count || 0
  };
};

export const getUserComments = async (userId: string, page = 1, limit = 10) => {
  const start = (page - 1) * limit;
  const end = start + limit - 1;
  
  const { data, error, count } = await supabase
    .from('post_comments')
    .select(`
      *,
      board_posts(title)
    `, { count: 'exact' })
    .eq('author_id', userId)
    .order('created_at', { ascending: false })
    .range(start, end);
    
  if (error) {
    console.error('Error fetching user comments:', error);
    throw error;
  }
  
  return {
    comments: data,
    totalCount: count || 0
  };
};

export const deletePost = async (postId: string) => {
  const { error } = await supabase
    .from('board_posts')
    .delete()
    .eq('id', postId);
    
  if (error) {
    console.error('Error deleting post:', error);
    throw error;
  }
  
  return true;
};

export const updatePost = async (
  postId: string,
  title: string,
  content: string,
  category: BoardCategory
) => {
  const { data, error } = await supabase
    .from('board_posts')
    .update({ 
      title, 
      content, 
      category,
      updated_at: new Date().toISOString()
    })
    .eq('id', postId)
    .select()
    .single();
    
  if (error) {
    console.error('Error updating post:', error);
    throw error;
  }
  
  return data;
};

export { 
  incrementViewCount
};
